import { useCallback, useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { Bell, Clock } from 'lucide-react';
import { useAuth } from '@/contexts/useAuth';
import {
  fetchNotificationPrefs,
  updateNotificationPrefs,
} from '@/lib/api/notifications';
import { track } from '@/lib/analytics';
import type { FriendlyError } from '@/lib/errors';
import {
  DEFAULT_NOTIFICATION_PREFS,
  type NotificationPrefs,
  type NotificationPrefsUpdate,
} from '@/types/notifications';
import Card from '@/components/ui/Card';
import Button from '@/components/ui/Button';
import Toggle from '@/components/ui/Toggle';
import ErrorAlert from '@/components/ui/ErrorAlert';
import HourSelect from './HourSelect';

function getLocalTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

function toUpdate(prefs: NotificationPrefsUpdate): NotificationPrefsUpdate {
  return {
    interview_reminders: prefs.interview_reminders,
    daily_practice: prefs.daily_practice,
    daily_practice_hour: prefs.daily_practice_hour,
    weekly_progress: prefs.weekly_progress,
    timezone: prefs.timezone,
  };
}

function prefsEqual(a: NotificationPrefsUpdate, b: NotificationPrefsUpdate): boolean {
  return (
    a.interview_reminders === b.interview_reminders &&
    a.daily_practice === b.daily_practice &&
    a.daily_practice_hour === b.daily_practice_hour &&
    a.weekly_progress === b.weekly_progress &&
    a.timezone === b.timezone
  );
}

interface ToggleRowProps {
  label: string;
  description: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
}

function ToggleRow({ label, description, checked, onChange, disabled }: ToggleRowProps) {
  return (
    <div className="flex items-start gap-4 py-3">
      <div className="min-w-0 flex-1">
        <p className="font-sans font-medium text-base text-text-primary">{label}</p>
        <p className="font-sans text-sm text-text-secondary mt-0.5">{description}</p>
      </div>
      <Toggle checked={checked} onChange={onChange} disabled={disabled} label={label} />
    </div>
  );
}

export default function NotificationPreferencesCard() {
  const { user } = useAuth();
  const [notificationPrefs, setNotificationPrefs] =
    useState<NotificationPrefs | null>(null);

  const current: NotificationPrefsUpdate = notificationPrefs
    ? toUpdate(notificationPrefs)
    : DEFAULT_NOTIFICATION_PREFS;

  const [draft, setDraft] = useState<NotificationPrefsUpdate>(current);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<FriendlyError | null>(null);

  const reload = useCallback(async () => {
    if (!user?.id) return;
    const prefs = await fetchNotificationPrefs(user.id);
    setNotificationPrefs(prefs);
  }, [user?.id]);

  useEffect(() => {
    reload();
  }, [reload]);

  useEffect(() => {
    setDraft(current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    notificationPrefs?.interview_reminders,
    notificationPrefs?.daily_practice,
    notificationPrefs?.daily_practice_hour,
    notificationPrefs?.weekly_progress,
    notificationPrefs?.timezone,
  ]);

  const dirty = !prefsEqual(draft, current);

  const update = <K extends keyof NotificationPrefsUpdate>(
    key: K,
    value: NotificationPrefsUpdate[K]
  ) => setDraft((prev) => ({ ...prev, [key]: value }));

  const cancel = () => {
    setDraft(current);
    setError(null);
  };

  const save = async () => {
    if (!user?.id || !dirty || saving) return;
    setSaving(true);
    setError(null);
    const payload: NotificationPrefsUpdate = { ...draft, timezone: getLocalTimezone() };
    const result = await updateNotificationPrefs(user.id, payload);
    if (!result.success) {
      setError(result.error ?? null);
      setSaving(false);
      return;
    }
    await reload();
    const toggleKeys = [
      'interview_reminders',
      'daily_practice',
      'weekly_progress',
    ] as const;
    for (const key of toggleKeys) {
      if (draft[key] !== current[key]) {
        track('notification_preference_toggled', {
          preference_key: key,
          enabled: draft[key],
        });
      }
    }
    setSaving(false);
    toast.success('Saved');
  };

  return (
    <Card>
      <div className="flex items-center gap-4 mb-2">
        <div className="w-10 h-10 rounded-full bg-primary-light flex items-center justify-center flex-shrink-0">
          <Bell size={20} className="text-primary" />
        </div>
        <div>
          <p className="font-display font-semibold text-lg text-text-primary leading-tight">
            Notifications
          </p>
          <p className="font-sans text-sm text-text-muted">
            Choose what we send you.
          </p>
        </div>
      </div>

      <div className="divide-y divide-border">
        <ToggleRow
          label="Interview reminders"
          description="Countdown nudges as your interview date approaches."
          checked={draft.interview_reminders}
          onChange={(v) => update('interview_reminders', v)}
          disabled={saving}
        />

        <div>
          <ToggleRow
            label="Daily practice nudges"
            description="A gentle daily reminder to practice your civics questions."
            checked={draft.daily_practice}
            onChange={(v) => update('daily_practice', v)}
            disabled={saving}
          />
          <AnimatePresence initial={false}>
            {draft.daily_practice && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="overflow-hidden"
              >
                <div className="pb-4 pl-1 flex items-center gap-3">
                  <Clock size={16} className="text-text-muted flex-shrink-0" />
                  <label
                    htmlFor="daily-nudge-hour"
                    className="font-sans text-sm text-text-secondary"
                  >
                    Remind me at
                  </label>
                  <div className="flex-1 max-w-[160px]">
                    <HourSelect
                      id="daily-nudge-hour"
                      value={draft.daily_practice_hour}
                      onChange={(h) => update('daily_practice_hour', h)}
                      disabled={saving}
                      ariaLabel="Daily practice reminder hour"
                    />
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        <ToggleRow
          label="Weekly progress email"
          description="A summary of your study streak and what to focus on next."
          checked={draft.weekly_progress}
          onChange={(v) => update('weekly_progress', v)}
          disabled={saving}
        />
      </div>

      {error && <ErrorAlert error={error} className="mt-4" />}

      <div className="mt-4 flex gap-3">
        <Button variant="ghost" onClick={cancel} disabled={!dirty || saving}>
          Cancel
        </Button>
        <div className="flex-1">
          <Button
            fullWidth
            onClick={save}
            disabled={!dirty || saving}
            loading={saving}
          >
            Save
          </Button>
        </div>
      </div>
    </Card>
  );
}
